import type { PerspectiveCamera } from "three";

export interface FreeCameraState {
  x: number;
  y: number;
  z: number;
  yaw: number;
  pitch: number;
}

export interface FreeCameraInput {
  forward: number;
  right: number;
  up: number;
  fast: boolean;
}

const PITCH_LIMIT = Math.PI / 2 - 0.01;
export const FREE_CAMERA_SPEED = 40;
export const FREE_CAMERA_FAST = 4;

/** Integra o voo livre de depuração (pura, testável). Yaw 0 olha para −z. */
export function stepFreeCamera(state: FreeCameraState, input: FreeCameraInput, dt: number): FreeCameraState {
  const speed = FREE_CAMERA_SPEED * (input.fast ? FREE_CAMERA_FAST : 1) * dt;
  const sin = Math.sin(state.yaw);
  const cos = Math.cos(state.yaw);
  const cp = Math.cos(state.pitch);
  const sp = Math.sin(state.pitch);
  // Para frente segue a direção do olhar (inclusive o pitch); lateral fica no plano.
  const fx = -sin * cp;
  const fy = sp;
  const fz = -cos * cp;
  return {
    ...state,
    x: state.x + (fx * input.forward + cos * input.right) * speed,
    y: state.y + (fy * input.forward + input.up) * speed,
    z: state.z + (fz * input.forward - sin * input.right) * speed,
  };
}

export function lookFreeCamera(state: FreeCameraState, dx: number, dy: number, sensitivity = 0.0022): FreeCameraState {
  const pitch = Math.max(-PITCH_LIMIT, Math.min(PITCH_LIMIT, state.pitch - dy * sensitivity));
  return { ...state, yaw: state.yaw - dx * sensitivity, pitch };
}

/** Teclado + mouse (pointer lock) para a câmera livre. */
export class FreeCameraController {
  state: FreeCameraState;
  private readonly keys = new Set<string>();
  private readonly element: HTMLElement;

  constructor(element: HTMLElement, initial: FreeCameraState) {
    this.element = element;
    this.state = initial;
    element.addEventListener("click", () => void element.requestPointerLock?.());
    document.addEventListener("mousemove", (e) => {
      if (document.pointerLockElement === element) this.state = lookFreeCamera(this.state, e.movementX, e.movementY);
    });
    window.addEventListener("keydown", (e) => this.keys.add(e.code));
    window.addEventListener("keyup", (e) => this.keys.delete(e.code));
    window.addEventListener("blur", () => this.keys.clear());
  }

  isDown(code: string): boolean {
    return this.keys.has(code);
  }

  update(dt: number, camera: PerspectiveCamera): void {
    const axis = (pos: string, neg: string) => (this.keys.has(pos) ? 1 : 0) - (this.keys.has(neg) ? 1 : 0);
    this.state = stepFreeCamera(
      this.state,
      {
        forward: axis("KeyW", "KeyS"),
        right: axis("KeyD", "KeyA"),
        up: axis("Space", "KeyC"),
        fast: this.keys.has("ShiftLeft") || this.keys.has("ShiftRight"),
      },
      dt,
    );
    camera.position.set(this.state.x, this.state.y, this.state.z);
    camera.rotation.set(this.state.pitch, this.state.yaw, 0, "YXZ");
  }
}
